import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DutyCycleTimeline } from '@/components/outputs/charts/DutyCycleTimeline';
import { PercentileCurveChart } from '@/components/outputs/charts/PercentileCurveChart';
import { formatNumber, formatDecimal } from '@/components/sizing/advancedInsightsShared';
import { Users, Clock, Sparkles, TrendingUp } from 'lucide-react';
import type { SizingNarrativeData } from '@/types/narrative';

type PersonaUsageModelingTabProps = {
  data: SizingNarrativeData;
};

export const PersonaUsageModelingTab: React.FC<PersonaUsageModelingTabProps> = ({ data }) => {
  const { personas, finalPlan, personaTimings, activePercentiles, kvPercentiles } = data;

  return (
    <div className="space-y-6">
      {/* Monte Carlo Process Explanation */}
      <Card className="border-primary/30 bg-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Monte Carlo Simulation Process
          </CardTitle>
          <CardDescription>How we model realistic user behavior and system dynamics</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="prose prose-sm max-w-none text-muted-foreground">
            <p>
              Our Monte Carlo (MC) simulation models real-world user behavior by treating each user as an independent agent
              following a <strong>probabilistic state machine</strong>:
            </p>
            
            <div className="my-4 p-4 rounded-lg bg-background border border-border">
              <div className="flex items-center justify-between max-w-2xl mx-auto">
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-blue-500/20 border-2 border-blue-500 flex items-center justify-center mb-2">
                    <Clock className="h-8 w-8 text-blue-500" />
                  </div>
                  <p className="text-sm font-medium">Reading</p>
                  <p className="text-xs text-muted-foreground">Digest response</p>
                </div>
                <div className="text-2xl text-muted-foreground">→</div>
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-purple-500/20 border-2 border-purple-500 flex items-center justify-center mb-2">
                    <Users className="h-8 w-8 text-purple-500" />
                  </div>
                  <p className="text-sm font-medium">Thinking</p>
                  <p className="text-xs text-muted-foreground">Formulate query</p>
                </div>
                <div className="text-2xl text-muted-foreground">→</div>
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center mb-2">
                    <TrendingUp className="h-8 w-8 text-emerald-500" />
                  </div>
                  <p className="text-sm font-medium">Generating</p>
                  <p className="text-xs text-muted-foreground">AI inference</p>
                </div>
              </div>
            </div>

            <ul className="space-y-2 my-4">
              <li>
                <strong>Poisson Arrivals:</strong> Users arrive at inference following a Poisson process with rate λ = 
                targetConcurrent / (readTime + thinkTime + serviceTime), modeling natural traffic volatility.
              </li>
              <li>
                <strong>Multiple Trials:</strong> We run thousands of simulation trials to capture statistical variance and compute
                robust percentiles (P50, P75, P95, P99).
              </li>
              <li>
                <strong>Horizon Window:</strong> Each trial simulates 10 minutes of real-time operation to capture peak load patterns
                and transient effects.
              </li>
              <li>
                <strong>P95 Sizing:</strong> We size infrastructure for the 95th percentile of concurrent demand, ensuring 95% of the time
                the system has adequate capacity, while avoiding over-provisioning for rare spikes.
              </li>
            </ul>

            <p className="text-xs italic border-l-2 border-primary pl-3">
              This approach provides more realistic capacity planning than naive "all users active simultaneously" assumptions,
              accounting for human duty cycles and stochastic arrival patterns.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Persona Timing Cards */}
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold mb-2">Persona Duty Cycle Analysis</h3>
          <p className="text-sm text-muted-foreground">
            Each persona has unique timing characteristics that affect system load and resource requirements
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          {personaTimings && personaTimings.length > 0 ? (
            personaTimings.map((timing) => (
              <DutyCycleTimeline
                key={timing.id}
                personaName={timing.name}
                readSeconds={timing.readSeconds}
                thinkSeconds={timing.thinkSeconds}
                ttftMs={timing.ttftMs}
                decodeTps={timing.decodeTps}
                outputTokens={timing.outputTokensPerConversation}
              />
            ))
          ) : (
            personas.map((persona) => (
              <Card key={persona.id}>
                <CardHeader>
                  <CardTitle className="text-base flex items-center justify-between">
                    {persona.label}
                    <Badge variant="outline">{formatDecimal(persona.sharePct, 0)}%</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Population:</span>
                    <span className="font-medium">{formatNumber(persona.population)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Input Tokens:</span>
                    <span className="font-medium">{formatNumber(persona.inputTokensPerConversation)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Output Tokens:</span>
                    <span className="font-medium">{formatNumber(persona.outputTokensPerConversation)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">KV GiB/Session:</span>
                    <span className="font-medium">{formatDecimal(persona.kvGiBPerSession, 2)}</span>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* P-Curves Visualization */}
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold mb-2">Percentile Distribution Curves</h3>
          <p className="text-sm text-muted-foreground">
            Statistical distribution of active sessions and KV cache requirements across simulation trials
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {activePercentiles && activePercentiles.length > 0 && (
            <PercentileCurveChart
              title="Active Sessions Distribution"
              description="Concurrent active inference sessions at different percentiles"
              data={activePercentiles}
              valueLabel="Active Sessions"
              color="hsl(var(--primary))"
            />
          )}

          {kvPercentiles && kvPercentiles.length > 0 && (
            <PercentileCurveChart
              title="KV Cache Retention Distribution"
              description="Number of sessions with KV cache resident in GPU memory"
              data={kvPercentiles}
              valueLabel="KV Sessions"
              color="hsl(var(--chart-2))"
            />
          )}
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <TrendingUp className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div className="space-y-2 text-sm">
                <p className="font-medium text-foreground">Why P95?</p>
                <p className="text-muted-foreground">
                  The <strong className="text-foreground">P95 (95th percentile)</strong> represents a balanced sizing target:
                  high enough to handle most load spikes without degradation, but avoiding costly over-provisioning for rare P99+ outliers.
                  This ensures <strong className="text-foreground">95% of the time</strong> your system has adequate capacity.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
